import React, { useState, useEffect, useRef, useContext } from "react";
import DynamicPdfGenerator from "./DynamicPdfGenerator";
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    Paper,
    CircularProgress,
    Snackbar,
    Alert,
    Grid,
    Button,
    Box,
    Checkbox,
    TextField,
} from "@mui/material";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import apiClient from "../../Helpers/apiClient";
import BackToCPQButton from "../../Components/backToCPQButton";
import { useLocation, useNavigate } from "react-router-dom";
import AuthContext from "../../Helpers/auth-context";
import Dynamics365NavigationBar from "../Dynamics365QuoteProductPages/Dynamics365NavigationBar";
import { useQuote } from "../Dynamics365QuoteProductPages/useQuote";
import AddressAutocomplete from "../../Components/AddressAutocomplete";
import FreightCalculationDisplay from "./FreightCalculationDisplay";

const ProductQuoteCalculator = () => {
    const { addProduct } = useQuote();
    const location = useLocation();
    const navigate = useNavigate();
    const { state } = useContext(AuthContext);

    // Flow stage management
    const [currentStage, setCurrentStage] = useState(1);
    const [stageCompletions, setStageCompletions] = useState({
        stage1: false, // Calculate Quote completed
        stage2: false, // Generate Model completed
        stage3: false, // Add to Dynamics completed
    });
    const [freightCalculationResult, setFreightCalculationResult] = useState(null);

    // Core form state with default values
    const [formData, setFormData] = useState({
        // Tank configuration
        tankDiameter: "",
        tankDepth: "",
        accessSize: "",
        accessCoverType: "",
        // Valve pit configuration
        valvePitDescription: "",
        valvePitAccessCoverSize: "",
        valvePitAccessCoverType: "",
        // Customer information
        customerName: "Default Customer",
        customerEmail: "default@email.com",
        customerPhone: "0000000000",
        projectName: "Default Project",
        projectAddress: "Default Address",
        notes: "Default Notes",
        // Pipe work configuration
        pipeWorkSize: "",
        pipeWorkMaterial: "",
        valveMaterial: "",
        // Pump configuration
        pumpModel: "",
        pump_model: "",
        numberOfPumps: 1,
        pumpOperation: 1,
        pumpSetup: "",
        guideRailType: "",
        controlType: "",
        // Flow and head calculations
        totalHead: "",
        flow: "",
        city: "",
        levelControlDescription: "",
        // Mixer configuration
        mixerType: "",
        mixerId: "",
        QuoteId: "",
        // User Selected controller
        controllerModelId: "", // Add this field
    });

    console.log("Form Data:", formData);

    // Head calculation state for dynamic head computation
    const [headCalculation, setHeadCalculation] = useState({
        calculateHead: false,
        staticHead: "",
        risingMainLength: "",
        risingMainSize: "",
        risingMainType: "",
    });

    // Quote pricing breakdown
    const [quoteBreakdown, setQuoteBreakdown] = useState({
        tankPrice: 0,
        accessCoverPrice: 0,
        valvePitPrice: 0,
        pipeWorkPrice: 0,
        pumpPrice: 0,
        mixerPrice: 0,
        freightPrice: 0,
        levelControlPrice: 0,
        valvePitAccessCoverPrice: 0,
        pedestalPrice: 0,
        guideRailPrice: 0,
        controllerPrice: 0,
        totalPrice: 0,
    });

    // UI state management
    const [showValvePit, setShowValvePit] = useState(false);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });
    const [controllerError, setControllerError] = useState(false);
    const [pumpDisplayLimit, setPumpDisplayLimit] = useState(3);
    const [dataveserQuoteProductQuantity, setDataverseQuoteProductQuantity] = useState(1);
    const [workItemId, setWorkItemId] = useState("");

    // Dropdown options state
    const [dimensions, setDimensions] = useState({ diameters: [], depths: [] });
    const [accessOptions, setAccessOptions] = useState({ sizes: [], coverTypes: [] });
    const [valvePitOptions, setValvePitOptions] = useState({ descriptions: [] });
    const [pipeWorkOptions, setPipeWorkOptions] = useState({
        sizes: [],
        pipeMaterials: [],
        valveMaterials: [],
    });
    const [pumpOptions, setPumpOptions] = useState({ supplierModels: [] });
    const [mixerOptions, setMixerOptions] = useState({ mixers: [] });
    // const [freightOptions, setFreightOptions] = useState({ cities: [] });
    const [levelControlOptions, setLevelControlOptions] = useState({ descriptions: [] });

    // Chart and pump performance state
    const [combinedChartData, setCombinedChartData] = useState([]);
    const [pdfChartData, setPdfChartData] = useState([]);
    const [pumpPerformanceCurve, setPumpPerformanceCurve] = useState([]);
    const [pumpPerformanceStatus, setPumpPerformanceStatus] = useState({
        loading: false,
        error: false,
        errorMessage: "",
    });
    const [recommendedPumps, setRecommendedPumps] = useState([]);
    const [recommendedPumpCurves, setRecommendedPumpCurves] = useState({});
    const [showRecommendations, setShowRecommendations] = useState(false);
    const [controllerModels, setControllerModels] = useState([]);

    // Chart container references
    const pdfChartContainerRef = useRef(null);

    // Static pump configuration options
    const pumpConfigurations = {
        numberOfPumps: [
            { value: 1, label: "Single" },
            { value: 2, label: "Dual" },
            { value: 3, label: "Triplex" },
        ],
        pumpOperationsByPumpCount: {
            1: [{ value: 1, label: "Duty" }],
            2: [
                { value: 1, label: "Duty/Standby" },
                { value: 2, label: "Duty/Duty" },
            ],
            3: [
                { value: 1, label: "Duty/Standby/Standby" },
                { value: 2, label: "Duty/Duty/Standby" },
                { value: 3, label: "Duty/Duty/Duty" },
            ],
        },
        pumpSetups: ["Freestanding", "On Guide Rails"],
    };

    // Reset function to go back to stage 1
    const resetToStage1 = () => {
        setCurrentStage(1);
        setStageCompletions({
            stage1: false,
            stage2: false,
            stage3: false,
        });
        setQuoteBreakdown({
            tankPrice: 0,
            accessCoverPrice: 0,
            valvePitPrice: 0,
            pipeWorkPrice: 0,
            pumpPrice: 0,
            mixerPrice: 0,
            freightPrice: 0,
            levelControlPrice: 0,
            valvePitAccessCoverPrice: 0,
            pedestalPrice: 0,
            guideRailPrice: 0,
            controllerPrice: 0,
            totalPrice: 0,
        });
        setWorkItemId("");
    };

    // Stage-based UI control functions
    const isButtonEnabled = (buttonType) => {
        switch (buttonType) {
            case "calculateQuote":
                return currentStage === 1;
            case "generateModel":
                return currentStage === 2 && stageCompletions.stage1;
            case "addToDynamics":
                return currentStage === 3 && stageCompletions.stage2;
            default:
                return false;
        }
    };

    const isFieldEnabled = (fieldType) => {
        switch (fieldType) {
            case "userInputs":
                return currentStage === 1;
            case "productQuantity":
                return currentStage === 3 && stageCompletions.stage2;
            default:
                return false;
        }
    };

    // Extract URL parameters and check authentication on component mount
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get("id");
        if (id) {
            setFormData((prev) => ({ ...prev, QuoteId: id }));
        }

        // Check authentication
        if (!state.isAuthenticated) {
            navigate("/login", { state: { returnTo: location.pathname + location.search } });
        } else {
            setLoading(false);
        }
    }, [location, state.isAuthenticated, navigate]);

    // Load all initial dropdown data on component mount
    useEffect(() => {
        fetchInitialData();
    }, []);

    /**
     * Fetches all initial dropdown options from API endpoints
     */
    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const endpoints = [
                "/api/quote/tank-dimensions",
                "/api/quote/access-cover-options",
                "/api/quote/valve-pit-options",
                "/api/quote/pipe-work-options",
                "/api/quote/pump-options",
                "/api/quote/mixer-options",
                "/api/quote/level-control-options",
            ];

            const responses = await Promise.all(endpoints.map((endpoint) => apiClient.get(endpoint)));

            setDimensions(responses[0].data);
            setAccessOptions(responses[1].data);
            setValvePitOptions(responses[2].data);
            setPipeWorkOptions(responses[3].data);
            setPumpOptions(responses[4].data);
            setMixerOptions(responses[5].data);
            // setFreightOptions(responses[6].data);
            setLevelControlOptions(responses[6].data);
        } catch (err) {
            showNotification(err.response?.data?.message || "Failed to load initial data", "error");
        } finally {
            setLoading(false);
        }
    };

    /**
     * Shows notification with message and severity
     */
    const showNotification = (message, severity = "success") => {
        setNotification({ open: true, message, severity });
    };

    /**
     * Maps valve pit description to corresponding access cover size
     */
    const mapValvePitDescriptionToAccessSize = (description) => {
        const mapping = {
            "600 x 600 x 600 with 600 x 600 Access Opening": "600 x 600",
            "600 x 900 x 900 with 600 x 600 Access Opening": "600 x 600",
            "600 x 900 x 900 with 600 x 900 Access Opening": "600 x 900",
            "900 x 900 x 900 with 600 x 900 Access Opening": "600 x 900",
            "900 x 900 x 900 with 900 x 900 Access Opening": "900 x 900",
            "1200 x 1200 x 1200 with 900 x 1200 Access Opening": "900 x 1200",
        };
        return mapping[description] || "";
    };

    /**
     * Calculates total head including friction loss based on pipe parameters
     */
    const calculateTotalHead = (staticHead, length, size, type, flowRate) => {
        const head = parseFloat(staticHead) || 0;
        const mainLength = parseFloat(length) || 0;
        const mainSize = parseFloat(size) || 0;
        const mainType = parseFloat(type) || 150;
        const flow = parseFloat(flowRate) || 0;

        if (!headCalculation.calculateHead || mainLength <= 0 || mainSize <= 0 || flow <= 0) {
            return head.toFixed(2);
        }

        try {
            const sizeInMeters = mainSize / 1000;
            const flowInCubicMetersPerSecond = flow / 1000;
            const frictionLoss =
                10.67 *
                (mainLength / (Math.pow(mainType, 1.85) * Math.pow(sizeInMeters, 4.87))) *
                Math.pow(flowInCubicMetersPerSecond, 1.85);

            if (isNaN(frictionLoss) || !isFinite(frictionLoss)) {
                return head.toFixed(2);
            }

            return (head + frictionLoss).toFixed(2);
        } catch (error) {
            console.error("Error in total head calculation:", error);
            return head.toFixed(2);
        }
    };
    // Add this function to your ProductQuoteCalculator
    const handleFreightPriceOverride = (newFreightPrice) => {
        setQuoteBreakdown((prev) => ({
            ...prev,
            freightPrice: newFreightPrice,
            totalPrice: prev.totalPrice - prev.freightPrice + newFreightPrice,
        }));
    };

    /**
     * Handles form input changes with special logic for valve pit and mixer selections
     */
    const handleChange = (event) => {
        const { name, value } = event.target;

        if (name === "valvePitDescription") {
            const mappedSize = mapValvePitDescriptionToAccessSize(value);
            setFormData((prev) => ({
                ...prev,
                valvePitDescription: value,
                valvePitAccessCoverSize: mappedSize,
            }));
        } else if (name === "mixerType") {
            const [mixerType, mixerId] = value.split("|");
            setFormData((prev) => ({
                ...prev,
                mixerType: mixerType || "",
                mixerId: mixerId === "null" || mixerId === "no-id" ? null : mixerId,
            }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    /**
     * Fetches pump recommendations based on flow and head requirements
     */
    const fetchPumpRecommendations = async (flow, head) => {
        try {
            const response = await apiClient.get("/api/pumpPerformance/pump-recommendations", {
                params: { flow, head },
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching pump recommendations:", error);
            return [];
        }
    };

    /**
     * Fetches pump performance curve data for a specific pump model
     */
    const fetchPumpPerformanceData = async (pumpModel) => {
        if (!pumpModel) {
            setPumpPerformanceCurve([]);
            setPumpPerformanceStatus({ loading: false, error: false, errorMessage: "" });
            return;
        }

        setPumpPerformanceStatus({ loading: true, error: false, errorMessage: "" });

        try {
            const response = await apiClient.post("/api/pumpPerformance/performance-curve", { pumpModel });
            setPumpPerformanceCurve(response.data);
            setPumpPerformanceStatus({ loading: false, error: false, errorMessage: "" });
        } catch (error) {
            console.error("Error fetching pump performance data:", error);
            setPumpPerformanceCurve([]);
            const errorMessage =
                error.response?.status === 404
                    ? `No performance data available for pump model: ${pumpModel}`
                    : "Error loading pump performance data. Please try again.";
            setPumpPerformanceStatus({ loading: false, error: true, errorMessage });
        }
    };

    /**
     * Submits form data to calculate quote pricing
     */
    const handleSubmit = async () => {
        // Validate required fields
        if (
            !formData.tankDiameter ||
            !formData.tankDepth ||
            !formData.accessSize ||
            !formData.accessCoverType ||
            (showValvePit &&
                (!formData.valvePitDescription ||
                    !formData.valvePitAccessCoverSize ||
                    !formData.valvePitAccessCoverType))
        ) {
            showNotification("Please fill in all required fields", "error");
            return;
        }

        const shouldCheck = !!formData.pumpModel && !!formData.controlType;
        setLoading(true);

        try {
            const response = await apiClient.post("/api/quote/calculate", formData);

            // Add freight breakdown calculation
            const freightBreakdownResponse = await apiClient.post(
                "/api/freight/calculate-breakdown",
                formData
            );
            setFreightCalculationResult(freightBreakdownResponse.data);

            // Update quote breakdown with API response
            setQuoteBreakdown({
                tankPrice: response.data.tankPrice ?? 0,
                accessCoverPrice: response.data.accessCoverPrice ?? 0,
                valvePitPrice: response.data.valvePitPrice ?? 0,
                pipeWorkPrice: response.data.pipeWorkPrice ?? 0,
                pumpPrice: response.data.pumpPrice ?? 0,
                mixerPrice: response.data.mixerPrice ?? 0,
                freightPrice: response.data.freightPrice ?? 0,
                levelControlPrice: response.data.levelControlPrice ?? 0,
                valvePitAccessCoverPrice: response.data.valvePitAccessCoverPrice ?? 0,
                pedestalPrice: response.data.pedestalPrice ?? 0,
                guideRailPrice: response.data.guideRailPrice ?? 0,
                controllerPrice: response.data.controllerPrice ?? 0,
                totalPrice: response.data.totalPrice ?? 0,
            });

            const hasControllerError = shouldCheck && response.data.controllerPrice === 0;
            setControllerError(hasControllerError);

            // Mark stage 1 as completed and move to stage 2
            setStageCompletions((prev) => ({ ...prev, stage1: true }));
            setCurrentStage(2);

            showNotification(
                hasControllerError
                    ? "Quote calculated, but no matching controller found in database."
                    : "Quote calculated successfully. You can now generate the model.",
                hasControllerError ? "warning" : "success"
            );
        } catch (err) {
            showNotification(err.response?.data?.message || "Failed to calculate quote", "error");
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handles PDF generation success
     */
    const handlePdfGenerationSuccess = () => {
        // Mark stage 2 as completed and move to stage 3
        setStageCompletions((prev) => ({ ...prev, stage2: true }));
        setCurrentStage(3);
        showNotification(
            "Model generated successfully. You can now add the product to Dynamics 365.",
            "success"
        );
    };

    /**
     * Adds the configured product to Dynamics 365 quote
     */
    const addProductToQuoteOnDataverse = async () => {
        setLoading(true);
        try {
            const inputdata = {
                // Core project data
                projectName: formData.projectName,
                projectAddress: formData.projectAddress,
                customerName: formData.customerName,
                customerEmail: formData.customerEmail,
                customerPhone: formData.customerPhone,
                notes: formData.notes,

                // Tank configuration
                tankDiameter: formData.tankDiameter,
                tankDepth: formData.tankDepth,

                // Pump configuration
                pumpModel: formData.pumpModel,
                numberOfPumps: formData.numberOfPumps,
                pumpSetup: formData.pumpSetup,
                pumpOperation: formData.pumpOperation,
                guideRailType: formData.guideRailType,
                flow: formData.flow,
                totalHead: formData.totalHead,

                // Access and valve configuration
                accessSize: formData.accessSize,
                accessCoverType: formData.accessCoverType,
                valvePitDescription: formData.valvePitDescription,
                valvePitAccessCoverSize: formData.valvePitAccessCoverSize,
                valvePitAccessCoverType: formData.valvePitAccessCoverType,

                // Pipe work and additional equipment
                pipeWorkSize: formData.pipeWorkSize,
                pipeWorkMaterial: formData.pipeWorkMaterial,
                valveMaterial: formData.valveMaterial,
                controlType: formData.controlType,
                levelControlDescription: formData.levelControlDescription,
                mixerId: formData.mixerId,
                mixerType: formData.mixerType,
                city: formData.city,

                // Head calculation data
                calculateHead: headCalculation.calculateHead,
                staticHead: headCalculation.staticHead,
                risingMainLength: headCalculation.risingMainLength,
                risingMainSize: headCalculation.risingMainSize,
                risingMainType: headCalculation.risingMainType,

                // Quote data
                QuoteId: formData.QuoteId,
                TemplateId: "892243f1-f2b2-f011-bbd3-000d3ad0ff6e",
                dataverseProductPrice: quoteBreakdown.totalPrice,
                dataveserQuoteProductQuantity,
                quoteBreakdown,
            };

            const response = await apiClient.post(
                "/api/PumpStationQuoteProduct/create-quote-product",
                inputdata
            );

            // Create product DTO for local state management
            const productDTO = {
                id: `pumpstation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                workItemId,
                quoteId: response.data.quoteProduct.quoteId,
                productType: "PumpStation",
                modelConfig: formData,
                priceBreakdown: quoteBreakdown,
                totalPrice: response.data.quoteProduct.price * response.data.quoteProduct.quantity,
                productName: response.data.quoteProduct.productName,
                partNumber: response.data.quoteProduct.partNumber,
                pcs_DetailDescriptions: response.data.quoteProduct.pcs_DetailDescriptions,
                quantity: response.data.quoteProduct.quantity,
                price: response.data.quoteProduct.price,
                addedAt: new Date().toISOString(),
            };

            addProduct(productDTO);

            // Mark stage 3 as completed and reset to stage 1
            setStageCompletions((prev) => ({ ...prev, stage3: true }));
            showNotification("Product added to quote successfully. Starting new configuration.", "success");

            // Reset after a short delay to show success message
            setTimeout(() => {
                resetToStage1();
            }, 2000);
        } catch (err) {
            showNotification(err.response?.data?.message || "Failed to add product to quote", "error");
        } finally {
            setLoading(false);
        }
    };

    // Effect to fetch pump performance data when pump model changes
    useEffect(() => {
        fetchPumpPerformanceData(formData.pumpModel);
    }, [formData.pumpModel]);

    // Effect to fetch pump recommendations when flow/head parameters change
    useEffect(() => {
        const getRecommendations = async () => {
            if (
                formData.flow &&
                formData.totalHead &&
                !isNaN(parseFloat(formData.flow)) &&
                !isNaN(parseFloat(formData.totalHead))
            ) {
                const totalFlow = parseFloat(formData.flow);
                const totalHead = parseFloat(formData.totalHead);

                // Calculate flow per pump based on configuration
                let numberOfDutyPumps = 1;
                let adjustedFlow = totalFlow;

                if (formData.numberOfPumps === 2 && formData.pumpOperation === 2) {
                    numberOfDutyPumps = 2;
                    adjustedFlow = totalFlow / 2;
                } else if (formData.numberOfPumps === 3 && formData.pumpOperation === 2) {
                    numberOfDutyPumps = 2;
                    adjustedFlow = totalFlow / 2;
                } else if (formData.numberOfPumps === 3 && formData.pumpOperation === 3) {
                    numberOfDutyPumps = 3;
                    adjustedFlow = totalFlow / 3;
                }

                const recommendations = await fetchPumpRecommendations(adjustedFlow, totalHead);

                // Process recommendations with duty pump information
                let processedRecommendations = recommendations.map((pump) => ({
                    ...pump,
                    individualFlow: pump.exactFlow,
                    combinedFlow: numberOfDutyPumps > 1 ? pump.exactFlow * numberOfDutyPumps : pump.exactFlow,
                    numberOfDutyPumps,
                }));

                // Filter for multi-pump setups
                if (numberOfDutyPumps > 1) {
                    processedRecommendations = processedRecommendations.filter(
                        (pump) => Math.abs(pump.combinedFlow - totalFlow) <= 1
                    );
                }

                setRecommendedPumps(processedRecommendations);
                setShowRecommendations(processedRecommendations.length > 0);

                // Fetch performance curves for recommended pumps
                const pumpCurves = {};
                await Promise.all(
                    processedRecommendations.map(async (pump) => {
                        try {
                            const response = await apiClient.post("/api/pumpPerformance/performance-curve", {
                                pumpModel: pump.supplierModel,
                            });
                            pumpCurves[pump.supplierModel] = response.data;
                        } catch (error) {
                            console.error(`Error fetching curve for ${pump.supplierModel}:`, error);
                            pumpCurves[pump.supplierModel] = [];
                        }
                    })
                );

                setRecommendedPumpCurves(pumpCurves);
            } else {
                setRecommendedPumps([]);
                setShowRecommendations(false);
                setRecommendedPumpCurves({});
            }
        };

        getRecommendations();
    }, [formData.flow, formData.totalHead, formData.numberOfPumps, formData.pumpOperation]);

    // Effect to sync pipe work settings with head calculation settings
    useEffect(() => {
        if (headCalculation.calculateHead) {
            const materialMapping = { 150: "PVC", 140: "Polyethylene" };
            setFormData((prev) => ({
                ...prev,
                pipeWorkSize: headCalculation.risingMainSize || "",
                pipeWorkMaterial: materialMapping[headCalculation.risingMainType] || "",
            }));
        }
    }, [headCalculation.calculateHead, headCalculation.risingMainSize, headCalculation.risingMainType]);

    /**
     * Generates combined chart data for pump performance visualization
     */
    const generateChartData = () => {
        let combinedData = [];

        // Add selected pump performance data
        if (pumpPerformanceCurve.length > 0) {
            const selectedPump = recommendedPumps.find((p) => p.supplierModel === formData.pumpModel);
            const numberOfDutyPumps = selectedPump?.numberOfDutyPumps || 1;

            const pumpData = pumpPerformanceCurve
                .filter((point) => point.flow !== null && point.flow !== undefined)
                .map((point) => ({
                    flow: point.flow * numberOfDutyPumps,
                    head: point.head,
                }));

            combinedData = [...pumpData];
        }

        // Add recommended pump curves
        recommendedPumps.slice(0, pumpDisplayLimit).forEach((pump) => {
            if (pump.supplierModel !== formData.pumpModel) {
                const curveData = recommendedPumpCurves[pump.supplierModel];
                if (curveData?.length > 0) {
                    curveData.forEach((point) => {
                        if (point.flow !== null && point.flow !== undefined) {
                            const existingPoint = combinedData.find(
                                (p) => Math.abs(p.flow - point.flow * pump.numberOfDutyPumps) < 0.01
                            );
                            if (existingPoint) {
                                existingPoint[`pump_${pump.supplierModel}`] = point.head;
                            } else {
                                combinedData.push({
                                    flow: point.flow * pump.numberOfDutyPumps,
                                    [`pump_${pump.supplierModel}`]: point.head,
                                });
                            }
                        }
                    });
                }
            }
        });

        // Add system curve if calculation mode is enabled
        if (headCalculation.calculateHead) {
            const staticHead = parseFloat(headCalculation.staticHead || 0);
            const risingMainLength = parseFloat(headCalculation.risingMainLength || 0);
            const risingMainSize = parseFloat(headCalculation.risingMainSize || 0);
            const risingMainType = parseFloat(headCalculation.risingMainType || 150);

            if (risingMainLength > 0 && risingMainSize > 0) {
                const maxFlow =
                    combinedData.length > 0 ? Math.max(...combinedData.map((p) => p.flow)) * 1.1 : 10;
                const numPoints = 12;
                const flowStep = maxFlow / (numPoints - 1);

                // Add system curve points
                for (let i = 0; i < numPoints; i++) {
                    const flow = i * flowStep;
                    const systemHead = parseFloat(
                        calculateTotalHead(staticHead, risingMainLength, risingMainSize, risingMainType, flow)
                    );

                    const existingPoint = combinedData.find((p) => Math.abs(p.flow - flow) < 0.01);
                    if (existingPoint) {
                        existingPoint.systemHead = systemHead;
                    } else {
                        combinedData.push({ flow, systemHead });
                    }
                }
            }
        }

        // Add operating points
        if (formData.flow && formData.totalHead) {
            const flow = parseFloat(formData.flow);
            const head = parseFloat(formData.totalHead);

            if (headCalculation.calculateHead) {
                combinedData.push({ flow, operatingPoint: head });
            } else {
                combinedData.push({ flow, manualHead: head });
            }
        }

        combinedData.sort((a, b) => a.flow - b.flow);
        setCombinedChartData(combinedData);
    };

    // Effect to generate chart data when dependencies change
    useEffect(() => {
        generateChartData();
    }, [
        headCalculation,
        formData.flow,
        formData.totalHead,
        formData.pumpModel,
        pumpPerformanceCurve,
        recommendedPumpCurves,
        pumpDisplayLimit,
    ]);

    // Generate PDF-specific chart data
    useEffect(() => {
        const generatePdfChartData = () => {
            let pdfData = [];

            // Add selected pump data
            if (pumpPerformanceCurve.length > 0) {
                const selectedPump = recommendedPumps.find((p) => p.supplierModel === formData.pumpModel);
                const numberOfDutyPumps = selectedPump?.numberOfDutyPumps || 1;

                pdfData = pumpPerformanceCurve
                    .filter((point) => point.flow !== null && point.flow !== undefined)
                    .map((point) => ({
                        flow: point.flow * numberOfDutyPumps,
                        head: point.head,
                    }));
            }

            // Add system curve data
            if (headCalculation.calculateHead) {
                const systemData = combinedChartData
                    .filter((point) => point.systemHead !== undefined)
                    .map((point) => ({ flow: point.flow, systemHead: point.systemHead }));

                systemData.forEach((point) => {
                    const existingPoint = pdfData.find((p) => Math.abs(p.flow - point.flow) < 0.01);
                    if (existingPoint) {
                        existingPoint.systemHead = point.systemHead;
                    } else {
                        pdfData.push({ flow: point.flow, systemHead: point.systemHead });
                    }
                });
            }

            // Add operating points
            if (formData.flow && formData.totalHead) {
                const flow = parseFloat(formData.flow);
                const head = parseFloat(formData.totalHead);

                if (headCalculation.calculateHead) {
                    pdfData.push({ flow, operatingPoint: head });
                } else {
                    pdfData.push({ flow, manualHead: head });
                }
            }

            pdfData.sort((a, b) => a.flow - b.flow);
            setPdfChartData(pdfData);
        };

        generatePdfChartData();
    }, [
        combinedChartData,
        pumpPerformanceCurve,
        formData.pumpModel,
        formData.flow,
        formData.totalHead,
        headCalculation.calculateHead,
        recommendedPumps,
    ]);

    useEffect(() => {
        if (
            !formData.controlType ||
            !formData.levelControlDescription ||
            !formData.mixerType ||
            !formData.pumpModel ||
            !formData.numberOfPumps
        ) {
            setControllerModels([]);
            return;
        }

        const fetchControllerModels = async () => {
            try {
                const response = await apiClient.post("/api/quote/get-controller-price-models", {
                    controlType: formData.controlType,
                    levelControlDescription: formData.levelControlDescription,
                    mixerType: formData.mixerType,
                    pumpModel: formData.pumpModel,
                    numberOfPumps: formData.numberOfPumps,
                });
                setControllerModels(response.data);
            } catch (err) {
                setControllerModels([]);
            }
        };

        fetchControllerModels();
    }, [
        formData.controlType,
        formData.mixerType,
        formData.levelControlDescription,
        formData.pumpModel,
        formData.numberOfPumps,
    ]);

    /**
     * Component for displaying pump recommendations based on flow/head requirements
     */
    const PumpRecommendations = () => {
        if (!showRecommendations || recommendedPumps.length === 0) return null;

        const handlePumpSelection = (supplierModel, model) => {
            setFormData((prev) => ({ ...prev, pumpModel: supplierModel, pump_model: model }));
        };

        const isMultipleDutyPumps =
            (formData.numberOfPumps === 2 && formData.pumpOperation === 2) ||
            (formData.numberOfPumps === 3 && formData.pumpOperation === 2) ||
            (formData.numberOfPumps === 3 && formData.pumpOperation === 3);

        return (
            <Box sx={{ mt: 2, mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="h6">Recommended Pumps</Typography>
                    <FormControl size="small" sx={{ width: 140 }}>
                        <Select
                            value={pumpDisplayLimit}
                            onChange={(e) => setPumpDisplayLimit(e.target.value)}
                            disabled={!isFieldEnabled("userInputs")}
                        >
                            {[1, 2, 3, 4, 5].map((limit) => (
                                <MenuItem key={limit} value={limit}>
                                    Showing {limit}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Based on your flow rate ({formData.flow} L/s) and total head ({formData.totalHead} m)
                    {isMultipleDutyPumps &&
                        ` - ${recommendedPumps[0]?.numberOfDutyPumps || 0} pumps working together`}
                </Typography>

                <Grid container spacing={2}>
                    {recommendedPumps.slice(0, pumpDisplayLimit).map((pump) => {
                        const isSelected = formData.pumpModel === pump.supplierModel;
                        const numberOfDutyPumps = pump.numberOfDutyPumps || 1;
                        const individualFlow = pump.exactFlow || 0;
                        const combinedFlow = pump.combinedFlow || individualFlow * numberOfDutyPumps;

                        return (
                            <Grid item xs={12} md={6} lg={4} key={pump.supplierModel}>
                                <Paper
                                    sx={{
                                        p: 2,
                                        border: isSelected ? "2px solid #1976d2" : "1px solid #e0e0e0",
                                        cursor: isFieldEnabled("userInputs") ? "pointer" : "not-allowed",
                                        transition: "all 0.2s ease",
                                        "&:hover": isFieldEnabled("userInputs")
                                            ? {
                                                  boxShadow: 3,
                                                  transform: "translateY(-2px)",
                                                  borderColor: "#1976d2",
                                              }
                                            : {},
                                        backgroundColor: isSelected ? "rgba(25, 118, 210, 0.04)" : "white",
                                        opacity: isFieldEnabled("userInputs") ? 1 : 0.6,
                                    }}
                                    onClick={() =>
                                        isFieldEnabled("userInputs") &&
                                        handlePumpSelection(pump.supplierModel, pump.model)
                                    }
                                >
                                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                        {pump.model || "Empty Model Name"}
                                    </Typography>
                                    <Typography variant="body2">
                                        {pump.kw}kW {pump.voltage}V - {pump.impellerType}
                                    </Typography>

                                    {isMultipleDutyPumps ? (
                                        <>
                                            <Typography variant="body2" color="text.secondary">
                                                Single pump: {individualFlow.toFixed(2)} L/s at{" "}
                                                {formData.totalHead}m
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="primary"
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                Combined: {combinedFlow.toFixed(2)} L/s with{" "}
                                                {numberOfDutyPumps} pumps
                                            </Typography>
                                        </>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            Exact flow at {formData.totalHead}m: {pump.exactFlow.toFixed(2)}{" "}
                                            L/s
                                        </Typography>
                                    )}

                                    <Button
                                        variant={isSelected ? "contained" : "outlined"}
                                        size="small"
                                        sx={{ mt: 1 }}
                                        disabled={!isFieldEnabled("userInputs")}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (isFieldEnabled("userInputs")) {
                                                handlePumpSelection(pump.supplierModel, pump.model);
                                            }
                                        }}
                                    >
                                        {isSelected ? "SELECTED" : "SELECT"}
                                    </Button>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            </Box>
        );
    };

    /**
     * Renders the hidden PDF chart for PDF generation
     */
    const renderHiddenPdfChart = () => (
        <Box
            ref={pdfChartContainerRef}
            className="pdf-chart"
            sx={{
                display: "none",
                width: "1000px",
                height: "600px",
                position: "absolute",
                top: 0,
                left: 0,
                backgroundColor: "white",
                zIndex: -1000,
            }}
            data-pdf-chart="true"
        >
            <Paper sx={{ p: 3, bgcolor: "white", width: "100%", height: "100%" }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Flow vs. Total Head Chart
                </Typography>
                <ResponsiveContainer width="100%" height={500}>
                    <LineChart data={pdfChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="flow"
                            domain={[0, (dataMax) => Math.ceil(dataMax * 1.1)]}
                            type="number"
                            label={{ value: "Flow (l/s)", position: "insideBottomRight", offset: -5 }}
                        />
                        <YAxis
                            type="number"
                            domain={[0, (dataMax) => Math.ceil(dataMax * 1.1)]}
                            label={{ value: "Head (m)", angle: -90, position: "insideLeft" }}
                        />
                        <Tooltip />
                        <Legend />

                        {/* System Curve */}
                        {headCalculation.calculateHead && (
                            <Line
                                type="monotone"
                                dataKey="systemHead"
                                name="Calculated Head-loss"
                                stroke="#000000"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                                connectNulls={true}
                            />
                        )}

                        {/* Selected pump performance line */}
                        {pumpPerformanceCurve.length > 0 && (
                            <Line
                                type="monotone"
                                dataKey="head"
                                name={
                                    formData.pump_model
                                        ? `${formData.pump_model} Performance`
                                        : "Empty Model Name"
                                }
                                stroke="#1976d2"
                                strokeWidth={2.5}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                                connectNulls={true}
                            />
                        )}

                        {/* Operating Points */}
                        {!headCalculation.calculateHead && formData.flow && formData.totalHead && (
                            <Line
                                type="monotone"
                                dataKey="manualHead"
                                name="Operating Point"
                                stroke="#ff0000"
                                strokeWidth={0}
                                dot={{ r: 6, stroke: "#ff0000", strokeWidth: 2, fill: "#ff0000" }}
                            />
                        )}

                        {headCalculation.calculateHead && formData.flow && formData.totalHead && (
                            <Line
                                type="monotone"
                                dataKey="operatingPoint"
                                name="Required Duty Point"
                                stroke="#ff0000"
                                strokeWidth={0}
                                dot={{ r: 6, stroke: "#ff0000", strokeWidth: 2, fill: "#ff0000" }}
                            />
                        )}
                    </LineChart>
                </ResponsiveContainer>
            </Paper>
        </Box>
    );

    /**
     * Formats price breakdown labels for display
     */
    const formatPriceLabel = (key) => {
        const labels = {
            tankPrice: "Tank Price",
            accessCoverPrice: "Access Cover Price",
            valvePitPrice: "Valve Pit Price",
            pipeWorkPrice: "Pipe Work Price",
            pumpPrice: "Pump Price",
            mixerPrice: "Mixer Price",
            freightPrice: "Freight Price",
            levelControlPrice: "Level Control Price",
            valvePitAccessCoverPrice: "Valve Pit Access Cover Price",
            pedestalPrice: "Pedestal Price",
            guideRailPrice: "Guide Rail Price",
            controllerPrice: "Controller Price",
        };
        return labels[key] || key;
    };

    /**
     * Stage indicator component
     */
    const StageIndicator = () => (
        <Box sx={{ mb: 3, p: 2, backgroundColor: "#f5f5f5", borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                Process Flow - Stage {currentStage} of 3
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {[1, 2, 3].map((stage) => (
                    <Box key={stage} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                backgroundColor:
                                    currentStage === stage
                                        ? "#1976d2"
                                        : stageCompletions[`stage${stage}`]
                                        ? "#4caf50"
                                        : "#e0e0e0",
                                color:
                                    currentStage === stage || stageCompletions[`stage${stage}`]
                                        ? "white"
                                        : "#666",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "bold",
                            }}
                        >
                            {stageCompletions[`stage${stage}`] ? "✓" : stage}
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{
                                color:
                                    currentStage === stage
                                        ? "#1976d2"
                                        : stageCompletions[`stage${stage}`]
                                        ? "#4caf50"
                                        : "#666",
                                fontWeight: currentStage === stage ? "bold" : "normal",
                            }}
                        >
                            {stage === 1
                                ? "Calculate Quote"
                                : stage === 2
                                ? "Generate Model"
                                : "Add to Dynamics 365"}
                        </Typography>
                        {stage < 3 && (
                            <Box sx={{ width: 20, height: 2, backgroundColor: "#e0e0e0", mx: 1 }} />
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );

    return (
        <Box sx={{ p: 3, pt: 0 }}>
            <Dynamics365NavigationBar />

            <Paper sx={{ p: 6, m: 3, mb: 5, boxShadow: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography variant="h5" sx={{ textAlign: "center", width: "100%" }}>
                        Pump Station Quote Calculator
                    </Typography>
                    {loading && <CircularProgress size={24} />}
                </Box>

                <StageIndicator />

                <Grid container spacing={4}>
                    {/* Tank and Access Section */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, backgroundColor: "#fafafa", boxShadow: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 3, pb: 1 }}>
                                Tank and Access Cover
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Tank Diameter</InputLabel>
                                        <Select
                                            name="tankDiameter"
                                            value={formData.tankDiameter}
                                            onChange={handleChange}
                                            label="Tank Diameter"
                                            disabled={loading || !isFieldEnabled("userInputs")}
                                        >
                                            {dimensions.diameters.map((d) => (
                                                <MenuItem key={d} value={d}>
                                                    {d}mm
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Tank Depth</InputLabel>
                                        <Select
                                            name="tankDepth"
                                            value={formData.tankDepth}
                                            onChange={handleChange}
                                            label="Tank Depth"
                                            disabled={loading || !isFieldEnabled("userInputs")}
                                        >
                                            {dimensions.depths.map((d) => (
                                                <MenuItem key={d} value={d}>
                                                    {d}mm
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Access Opening Size</InputLabel>
                                        <Select
                                            name="accessSize"
                                            value={formData.accessSize}
                                            onChange={handleChange}
                                            label="Access Opening Size"
                                            disabled={loading || !isFieldEnabled("userInputs")}
                                        >
                                            {accessOptions.sizes.map((size) => (
                                                <MenuItem key={size} value={size}>
                                                    {size}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Access Cover Type</InputLabel>
                                        <Select
                                            name="accessCoverType"
                                            value={formData.accessCoverType}
                                            onChange={handleChange}
                                            label="Access Cover Type"
                                            disabled={loading || !isFieldEnabled("userInputs")}
                                        >
                                            <MenuItem value="Not Required">Not Required</MenuItem>
                                            {accessOptions.coverTypes.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Valve Pit Section */}
                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                            <Checkbox
                                checked={showValvePit}
                                onChange={(e) => {
                                    if (!isFieldEnabled("userInputs")) return;
                                    setShowValvePit(e.target.checked);
                                    if (!e.target.checked) {
                                        setFormData((prev) => ({
                                            ...prev,
                                            valvePitDescription: "",
                                            valvePitAccessCoverType: "",
                                            valvePitAccessCoverSize: "",
                                        }));
                                    }
                                }}
                                disabled={!isFieldEnabled("userInputs")}
                            />
                            <Typography>Is Valve Pit Required?</Typography>
                        </Box>
                        {showValvePit && (
                            <Paper sx={{ p: 3, backgroundColor: "#fafafa", boxShadow: 2, borderRadius: 2 }}>
                                <Typography variant="h6" sx={{ mb: 3, pb: 1 }}>
                                    Valve Pit Configuration
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Valve Pit Type</InputLabel>
                                            <Select
                                                name="valvePitDescription"
                                                value={formData.valvePitDescription}
                                                onChange={handleChange}
                                                label="Valve Pit Type"
                                                disabled={loading || !isFieldEnabled("userInputs")}
                                            >
                                                {valvePitOptions.descriptions.map((description) => (
                                                    <MenuItem key={description} value={description}>
                                                        {description}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Valve Pit Access Cover Type</InputLabel>
                                            <Select
                                                name="valvePitAccessCoverType"
                                                value={formData.valvePitAccessCoverType}
                                                onChange={handleChange}
                                                label="Valve Pit Access Cover Type"
                                                disabled={loading || !isFieldEnabled("userInputs")}
                                            >
                                                {accessOptions.coverTypes.map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        {type}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}
                    </Grid>

                    {/* Pump Configuration Section */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, backgroundColor: "#fafafa", boxShadow: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 3, pb: 1 }}>
                                Pump Configuration
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>No. of Pumps</InputLabel>
                                        <Select
                                            name="numberOfPumps"
                                            value={formData.numberOfPumps}
                                            onChange={(e) => {
                                                if (!isFieldEnabled("userInputs")) return;
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    numberOfPumps: e.target.value,
                                                    pumpOperation: "",
                                                }));
                                            }}
                                            label="No. of Pumps"
                                            disabled={loading || !isFieldEnabled("userInputs")}
                                        >
                                            {pumpConfigurations.numberOfPumps.map((pump) => (
                                                <MenuItem key={pump.value} value={pump.value}>
                                                    {pump.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Pump Operation</InputLabel>
                                        <Select
                                            name="pumpOperation"
                                            value={formData.pumpOperation}
                                            onChange={handleChange}
                                            label="Pump Operation"
                                            disabled={
                                                loading ||
                                                !isFieldEnabled("userInputs") ||
                                                !formData.numberOfPumps
                                            }
                                        >
                                            {formData.numberOfPumps &&
                                                pumpConfigurations.pumpOperationsByPumpCount[
                                                    formData.numberOfPumps
                                                ].map((operation) => (
                                                    <MenuItem key={operation.label} value={operation.value}>
                                                        {operation.label}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Pump Setup</InputLabel>
                                        <Select
                                            name="pumpSetup"
                                            value={formData.pumpSetup}
                                            onChange={handleChange}
                                            label="Pump Setup"
                                            disabled={
                                                loading ||
                                                !isFieldEnabled("userInputs") ||
                                                !formData.numberOfPumps
                                            }
                                        >
                                            {pumpConfigurations.pumpSetups.map((setup) => (
                                                <MenuItem key={setup} value={setup}>
                                                    {setup}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {formData.pumpSetup === "On Guide Rails" && (
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <InputLabel>Guide Rail Type</InputLabel>
                                            <Select
                                                name="guideRailType"
                                                value={formData.guideRailType}
                                                onChange={handleChange}
                                                label="Guide Rail Type"
                                                disabled={loading || !isFieldEnabled("userInputs")}
                                            >
                                                <MenuItem value="">Select</MenuItem>
                                                <MenuItem value="Galvanised Steel">Galvanised Steel</MenuItem>
                                                <MenuItem value="Stainless Steel">Stainless Steel</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                        <Checkbox
                                            checked={headCalculation.calculateHead}
                                            onChange={(e) => {
                                                if (!isFieldEnabled("userInputs")) return;
                                                const checked = e.target.checked;
                                                setHeadCalculation((prev) => ({
                                                    ...prev,
                                                    calculateHead: checked,
                                                    staticHead: checked ? prev.staticHead : "",
                                                    risingMainLength: checked ? prev.risingMainLength : "",
                                                    risingMainSize: checked ? prev.risingMainSize : "",
                                                    risingMainType: checked ? prev.risingMainType : "",
                                                }));

                                                if (!checked) {
                                                    setFormData((prev) => ({ ...prev, totalHead: "" }));
                                                } else {
                                                    setHeadCalculation((prev) => ({
                                                        ...prev,
                                                        risingMainSize:
                                                            prev.risingMainSize || formData.pipeWorkSize,
                                                        risingMainType:
                                                            prev.risingMainType ||
                                                            (formData.pipeWorkMaterial === "PVC"
                                                                ? "150"
                                                                : formData.pipeWorkMaterial === "Polyethylene"
                                                                ? "140"
                                                                : ""),
                                                    }));
                                                }
                                            }}
                                            disabled={!isFieldEnabled("userInputs")}
                                        />
                                        <Typography>Calculate Head</Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Flow (l/s)"
                                        name="flow"
                                        value={formData.flow}
                                        onChange={(e) => {
                                            if (!isFieldEnabled("userInputs")) return;
                                            const newValue = e.target.value;
                                            if (
                                                headCalculation.calculateHead &&
                                                headCalculation.staticHead &&
                                                headCalculation.risingMainLength &&
                                                headCalculation.risingMainSize &&
                                                headCalculation.risingMainType
                                            ) {
                                                const calculatedHead = calculateTotalHead(
                                                    headCalculation.staticHead,
                                                    headCalculation.risingMainLength,
                                                    headCalculation.risingMainSize,
                                                    headCalculation.risingMainType,
                                                    newValue
                                                );
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    flow: newValue,
                                                    totalHead: calculatedHead,
                                                }));
                                            } else {
                                                setFormData((prev) => ({ ...prev, flow: newValue }));
                                            }
                                        }}
                                        disabled={loading || !isFieldEnabled("userInputs")}
                                        type="number"
                                        InputProps={{ inputProps: { min: 0 } }}
                                    />
                                </Grid>

                                {!headCalculation.calculateHead ? (
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Total Head (m)"
                                            name="totalHead"
                                            value={formData.totalHead}
                                            onChange={handleChange}
                                            disabled={loading || !isFieldEnabled("userInputs")}
                                            type="number"
                                            InputProps={{ inputProps: { min: 0 } }}
                                        />
                                    </Grid>
                                ) : (
                                    <>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle1" sx={{ mt: 1 }}>
                                                Calculated Total Head: {formData.totalHead || "0"} m
                                            </Typography>
                                        </Grid>
                                        {/* Head calculation input fields */}
                                        {[
                                            { label: "Static Head (m)", key: "staticHead" },
                                            { label: "Rising Main Length (m)", key: "risingMainLength" },
                                        ].map(({ label, key }) => (
                                            <Grid item xs={12} md={6} key={key}>
                                                <TextField
                                                    fullWidth
                                                    label={label}
                                                    value={headCalculation[key]}
                                                    onChange={(e) => {
                                                        if (!isFieldEnabled("userInputs")) return;
                                                        const newValue = e.target.value;
                                                        setHeadCalculation((prev) => ({
                                                            ...prev,
                                                            [key]: newValue,
                                                        }));
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            totalHead: calculateTotalHead(
                                                                key === "staticHead"
                                                                    ? newValue
                                                                    : headCalculation.staticHead,
                                                                key === "risingMainLength"
                                                                    ? newValue
                                                                    : headCalculation.risingMainLength,
                                                                headCalculation.risingMainSize,
                                                                headCalculation.risingMainType,
                                                                formData.flow
                                                            ),
                                                        }));
                                                    }}
                                                    disabled={loading || !isFieldEnabled("userInputs")}
                                                    type="number"
                                                    InputProps={{ inputProps: { min: 0 } }}
                                                />
                                            </Grid>
                                        ))}

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Rising Main Size (mm)</InputLabel>
                                                <Select
                                                    value={headCalculation.risingMainSize}
                                                    onChange={(e) => {
                                                        if (!isFieldEnabled("userInputs")) return;
                                                        const newValue = e.target.value;
                                                        setHeadCalculation((prev) => ({
                                                            ...prev,
                                                            risingMainSize: newValue,
                                                        }));
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            pipeWorkSize: newValue,
                                                            totalHead: calculateTotalHead(
                                                                headCalculation.staticHead,
                                                                headCalculation.risingMainLength,
                                                                newValue,
                                                                headCalculation.risingMainType,
                                                                formData.flow
                                                            ),
                                                        }));
                                                    }}
                                                    label="Rising Main Size (mm)"
                                                    disabled={loading || !isFieldEnabled("userInputs")}
                                                >
                                                    {pipeWorkOptions.sizes.map((size) => (
                                                        <MenuItem key={size} value={size}>
                                                            {size}mm
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Rising Main Type</InputLabel>
                                                <Select
                                                    value={headCalculation.risingMainType}
                                                    onChange={(e) => {
                                                        if (!isFieldEnabled("userInputs")) return;
                                                        const newValue = e.target.value;
                                                        const materialMapping = {
                                                            150: "PVC",
                                                            140: "Polyethylene",
                                                        };
                                                        setHeadCalculation((prev) => ({
                                                            ...prev,
                                                            risingMainType: newValue,
                                                        }));
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            pipeWorkMaterial: materialMapping[newValue] || "",
                                                            totalHead: calculateTotalHead(
                                                                headCalculation.staticHead,
                                                                headCalculation.risingMainLength,
                                                                headCalculation.risingMainSize,
                                                                newValue,
                                                                formData.flow
                                                            ),
                                                        }));
                                                    }}
                                                    label="Rising Main Type"
                                                    disabled={loading || !isFieldEnabled("userInputs")}
                                                >
                                                    <MenuItem value="150">PVC</MenuItem>
                                                    <MenuItem value="140">Polyethylene</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </>
                                )}

                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Pump Model</InputLabel>
                                        <Select
                                            name="pumpModel"
                                            value={formData.pumpModel || ""}
                                            onChange={handleChange}
                                            label="Pump Model"
                                            disabled={loading || !isFieldEnabled("userInputs")}
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            {pumpOptions.supplierModels.map((pump) => (
                                                <MenuItem
                                                    key={pump.id}
                                                    value={pump.supplierModel}
                                                    sx={{
                                                        backgroundColor:
                                                            formData.pumpModel === pump.supplierModel
                                                                ? "rgba(25, 118, 210, 0.08)"
                                                                : "transparent",
                                                    }}
                                                >
                                                    {`${pump.model || "No Model Name"} - ${pump.kw}kW ${
                                                        pump.voltage
                                                    }V - ${pump.impellerType}`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <PumpRecommendations />
                                </Grid>

                                {pumpPerformanceStatus.loading && (
                                    <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
                                        <CircularProgress size={24} sx={{ mr: 1 }} />
                                        <Typography variant="body2">
                                            Loading pump performance data...
                                        </Typography>
                                    </Box>
                                )}

                                {pumpPerformanceStatus.error && (
                                    <Alert severity="warning" sx={{ mt: 2 }}>
                                        {pumpPerformanceStatus.errorMessage}
                                    </Alert>
                                )}
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Flow vs. Total Head Chart */}
                    <Grid item xs={12}>
                        <Paper sx={{ mt: 3, p: 3, bgcolor: "white" }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Flow vs. Total Head Chart
                            </Typography>
                            <ResponsiveContainer width="100%" height={400}>
                                {formData.pumpModel && pumpPerformanceStatus.error ? (
                                    <text
                                        x="50%"
                                        y="50%"
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        style={{ fill: "#f44336", fontWeight: "bold" }}
                                    >
                                        No performance data available for this pump model
                                    </text>
                                ) : (
                                    <LineChart
                                        data={combinedChartData}
                                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="flow"
                                            domain={[0, (dataMax) => Math.ceil(dataMax * 1.1)]}
                                            type="number"
                                            label={{
                                                value: "Flow (l/s)",
                                                position: "insideBottomRight",
                                                offset: -5,
                                            }}
                                        />
                                        <YAxis
                                            type="number"
                                            domain={[0, (dataMax) => Math.ceil(dataMax * 1.1)]}
                                            label={{ value: "Head (m)", angle: -90, position: "insideLeft" }}
                                        />
                                        <Tooltip />
                                        <Legend />

                                        {/* System Curve */}
                                        {headCalculation.calculateHead && (
                                            <Line
                                                type="monotone"
                                                dataKey="systemHead"
                                                name="Calculated Head-loss"
                                                stroke="#000000"
                                                strokeWidth={2}
                                                dot={{ r: 3 }}
                                                activeDot={{ r: 5 }}
                                                connectNulls={true}
                                            />
                                        )}

                                        {/* Primary selected pump performance line */}
                                        {pumpPerformanceCurve.length > 0 && (
                                            <Line
                                                type="monotone"
                                                dataKey="head"
                                                name={
                                                    formData.pump_model
                                                        ? `${formData.pump_model} Performance`
                                                        : "Empty Model Name"
                                                }
                                                stroke="#1976d2"
                                                strokeWidth={2.5}
                                                dot={{ r: 3 }}
                                                activeDot={{ r: 5 }}
                                                connectNulls={true}
                                            />
                                        )}

                                        {/* Recommended pump performance lines */}
                                        {recommendedPumps.slice(0, pumpDisplayLimit).map((pump, index) => {
                                            if (pump.supplierModel === formData.pumpModel) return null;
                                            const colorOptions = [
                                                "#8884d8",
                                                "#82ca9d",
                                                "#ffc658",
                                                "#ff7300",
                                                "#0088FE",
                                                "#00C49F",
                                                "#FFBB28",
                                            ];
                                            const color = colorOptions[index % colorOptions.length];

                                            return (
                                                <Line
                                                    key={pump.supplierModel}
                                                    type="monotone"
                                                    dataKey={`pump_${pump.supplierModel}`}
                                                    name={pump.model || "Empty Model Name"}
                                                    stroke={color}
                                                    strokeWidth={1.5}
                                                    strokeDasharray="5 5"
                                                    dot={{ r: 2 }}
                                                    activeDot={{ r: 4 }}
                                                    connectNulls={true}
                                                />
                                            );
                                        })}

                                        {/* Operating Points */}
                                        {!headCalculation.calculateHead &&
                                            formData.flow &&
                                            formData.totalHead && (
                                                <Line
                                                    type="monotone"
                                                    dataKey="manualHead"
                                                    name="Required Duty Point"
                                                    stroke="#ff0000"
                                                    strokeWidth={0}
                                                    dot={{
                                                        r: 6,
                                                        stroke: "#ff0000",
                                                        strokeWidth: 2,
                                                        fill: "#ff0000",
                                                    }}
                                                />
                                            )}

                                        {headCalculation.calculateHead &&
                                            formData.flow &&
                                            formData.totalHead && (
                                                <Line
                                                    type="monotone"
                                                    dataKey="operatingPoint"
                                                    name="Required Duty Point"
                                                    stroke="#ff0000"
                                                    strokeWidth={0}
                                                    dot={{
                                                        r: 6,
                                                        stroke: "#ff0000",
                                                        strokeWidth: 2,
                                                        fill: "#ff0000",
                                                    }}
                                                />
                                            )}
                                    </LineChart>
                                )}
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Pipe Work Section */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, backgroundColor: "#fafafa", boxShadow: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 3, pb: 1 }}>
                                Pipe Work Configuration
                            </Typography>
                            <Grid container spacing={2}>
                                {!headCalculation.calculateHead ? (
                                    <>
                                        <Grid item xs={12} md={4}>
                                            <FormControl fullWidth>
                                                <InputLabel>Pipe Size</InputLabel>
                                                <Select
                                                    name="pipeWorkSize"
                                                    value={formData.pipeWorkSize}
                                                    onChange={handleChange}
                                                    label="Pipe Size"
                                                    disabled={loading || !isFieldEnabled("userInputs")}
                                                >
                                                    {pipeWorkOptions.sizes.map((size) => (
                                                        <MenuItem key={size} value={size}>
                                                            {size}mm
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <FormControl fullWidth>
                                                <InputLabel>Pipe Material</InputLabel>
                                                <Select
                                                    name="pipeWorkMaterial"
                                                    value={formData.pipeWorkMaterial}
                                                    onChange={handleChange}
                                                    label="Pipe Material"
                                                    disabled={
                                                        loading ||
                                                        !isFieldEnabled("userInputs") ||
                                                        !formData.pipeWorkSize
                                                    }
                                                >
                                                    {pipeWorkOptions.pipeMaterials.map((material) => (
                                                        <MenuItem key={material} value={material}>
                                                            {material}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </>
                                ) : (
                                    <Grid item xs={12} md={8}>
                                        <Typography variant="body2" color="text.secondary">
                                            Using Rising Main Size (
                                            {headCalculation.risingMainSize || "not set"}) mm and Rising Main
                                            Type (
                                            {headCalculation.risingMainType === "150"
                                                ? "PVC"
                                                : headCalculation.risingMainType === "140"
                                                ? "Polyethylene"
                                                : "not set"}
                                            ) values from the Pump Configuration section.
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Valve Material</InputLabel>
                                        <Select
                                            name="valveMaterial"
                                            value={formData.valveMaterial}
                                            onChange={handleChange}
                                            label="Valve Material"
                                            disabled={
                                                loading ||
                                                !isFieldEnabled("userInputs") ||
                                                (!headCalculation.calculateHead &&
                                                    (!formData.pipeWorkSize || !formData.pipeWorkMaterial)) ||
                                                (headCalculation.calculateHead &&
                                                    (!headCalculation.risingMainSize ||
                                                        !headCalculation.risingMainType))
                                            }
                                        >
                                            {pipeWorkOptions.valveMaterials.map((material) => (
                                                <MenuItem key={material} value={material}>
                                                    {material}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Additional Equipment Section */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, backgroundColor: "#fafafa", boxShadow: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 3, pb: 1 }}>
                                Additional Settings
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Mixer Type</InputLabel>
                                        <Select
                                            name="mixerType"
                                            value={
                                                formData.mixerType === "Not Required"
                                                    ? "Not Required|null"
                                                    : `${formData.mixerType}|${formData.mixerId || "no-id"}`
                                            }
                                            onChange={handleChange}
                                            label="Mixer Type"
                                            disabled={loading || !isFieldEnabled("userInputs")}
                                        >
                                            <MenuItem value="Not Required|null">Not Required</MenuItem>
                                            {mixerOptions.mixers.map((mixer) => (
                                                <MenuItem
                                                    key={mixer.id || `mixer-${mixer.mixerType}`}
                                                    value={`${mixer.mixerType}|${mixer.id || "no-id"}`}
                                                >
                                                    {mixer.mixerType}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Level Control</InputLabel>
                                        <Select
                                            name="levelControlDescription"
                                            value={formData.levelControlDescription}
                                            onChange={handleChange}
                                            label="Level Control"
                                            disabled={loading || !isFieldEnabled("userInputs")}
                                        >
                                            {levelControlOptions.descriptions.map((description) => (
                                                <MenuItem key={description} value={description}>
                                                    {description}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Control Type</InputLabel>
                                        <Select
                                            name="controlType"
                                            value={formData.controlType}
                                            onChange={handleChange}
                                            label="Control Type"
                                            disabled={loading || !isFieldEnabled("userInputs")}
                                        >
                                            <MenuItem value="Soft Starter">Soft Starter</MenuItem>
                                            <MenuItem value="Direct Online">Direct Online</MenuItem>
                                            <MenuItem value="VFD">VFD</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {controllerModels.length > 1 && (
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <InputLabel>Controller Model</InputLabel>
                                            <Select
                                                name="controllerModelId"
                                                value={formData.controllerModelId}
                                                onChange={handleChange}
                                                label="Controller Model"
                                            >
                                                <MenuItem value="">Select</MenuItem>
                                                {controllerModels.map((controller) => (
                                                    <MenuItem key={controller.id} value={controller.id}>
                                                        {controller.model || `Controller ${controller.id}`}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Typography variant="body1" color="text.secondary" sx={{}}>
                                            Destination Address
                                        </Typography>
                                    </Box>
                                    <FormControl fullWidth>
                                        <AddressAutocomplete onAddressSelect={setFormData} />
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Calculate Quote Button */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading || !isButtonEnabled("calculateQuote")}
                        sx={{
                            opacity: isButtonEnabled("calculateQuote") ? 1 : 0.6,
                        }}
                    >
                        Calculate Quote
                    </Button>
                </Box>

                {/* Quote Breakdown Section */}
                {quoteBreakdown.totalPrice > 0 && (
                    <>
                        {renderHiddenPdfChart()}
                        <DynamicPdfGenerator
                            formData={formData}
                            quoteBreakdown={quoteBreakdown}
                            chartRef={pdfChartContainerRef}
                            headCalculation={headCalculation}
                            passWorkItemIdToParent={(data) => setWorkItemId(data)}
                            onGenerationSuccess={handlePdfGenerationSuccess}
                            isEnabled={isButtonEnabled("generateModel")}
                        />

                        <FreightCalculationDisplay
                            calculationResult={freightCalculationResult}
                            onFreightPriceOverride={handleFreightPriceOverride}
                        />
                        <Paper sx={{ mt: 3, p: 2, bgcolor: "grey.50" }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Quote Summary
                            </Typography>

                            {controllerError && (
                                <Alert severity="warning" sx={{ mb: 3 }}>
                                    No matching controller found in the database for your selected pump and
                                    control type.
                                </Alert>
                            )}

                            <Grid container spacing={2}>
                                {Object.entries(quoteBreakdown).map(
                                    ([key, value]) =>
                                        key !== "totalPrice" && (
                                            <Grid item xs={12} md={4} key={key}>
                                                <Typography variant="subtitle1">
                                                    {formatPriceLabel(key)}:
                                                </Typography>
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <Typography variant="h6">${value.toFixed(2)}</Typography>
                                                    {key === "controllerPrice" &&
                                                        controllerError &&
                                                        value === 0 && (
                                                            <Typography
                                                                variant="caption"
                                                                color="error"
                                                                sx={{ ml: 1 }}
                                                            >
                                                                No match found
                                                            </Typography>
                                                        )}
                                                </Box>
                                            </Grid>
                                        )
                                )}

                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" sx={{ mt: 2 }}>
                                        Total Price:
                                    </Typography>
                                    <Typography variant="h4" color="primary">
                                        ${quoteBreakdown.totalPrice.toFixed(2)}
                                    </Typography>
                                    {controllerError && (
                                        <Typography variant="body2" color="error">
                                            *Price does not include controller
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>

                            <Box sx={{ display: "flex", mt: 3 }}>
                                <TextField
                                    label="Product Quantity"
                                    type="number"
                                    value={dataveserQuoteProductQuantity}
                                    onChange={(event) => {
                                        if (!isFieldEnabled("productQuantity")) return;
                                        const value = event.target.value;
                                        if (value === "" || (/^\d+$/.test(value) && parseInt(value) > 0)) {
                                            setDataverseQuoteProductQuantity(
                                                value === "" ? 1 : parseInt(value)
                                            );
                                        }
                                    }}
                                    variant="outlined"
                                    size="small"
                                    inputProps={{ min: 1, step: 1 }}
                                    sx={{
                                        width: 140,
                                        marginRight: 3,
                                        opacity: isFieldEnabled("productQuantity") ? 1 : 0.6,
                                    }}
                                    error={dataveserQuoteProductQuantity < 1}
                                    helperText={
                                        dataveserQuoteProductQuantity < 1 ? "Quantity must be at least 1" : ""
                                    }
                                    disabled={!isFieldEnabled("productQuantity")}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={addProductToQuoteOnDataverse}
                                    disabled={loading || !isButtonEnabled("addToDynamics")}
                                    startIcon={
                                        loading ? <CircularProgress size={20} color="inherit" /> : null
                                    }
                                    sx={{
                                        minWidth: 280,
                                        opacity: isButtonEnabled("addToDynamics") ? 1 : 0.6,
                                    }}
                                >
                                    {loading
                                        ? "Adding to Dynamics 365..."
                                        : "Add This Product to Dynamic 365"}
                                </Button>
                            </Box>
                        </Paper>
                    </>
                )}
            </Paper>

            <BackToCPQButton />

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
            >
                <Alert
                    onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
                    severity={notification.severity}
                    sx={{ width: "100%" }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ProductQuoteCalculator;
